import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getUserForUI } from "../../store/reducer/user/userSlice";
import { getOpportunityById } from "../../store/reducer/opportunity/opportunitySlice";
import { addDrawing } from "../../store/reducer/drawing/drawingSlice";

function AddDrawing() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    requested_by: Yup.string().required("please select requested by"),
    date_required: Yup.string().required("please select date required"),
    status: Yup.string().required("please enter opportunity name"),
  });

  const { options = [] } = useSelector((state) => state.ddOptions);

  const [opportunityData, setOpportunityData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(getDdOptionForUI());
    dispatch(getOpportunityById(id)).then((result) => {
      if (result.payload) {
        setOpportunityData(result.payload);
      }
    });
    dispatch(getUserForUI()).then((result) => {
      if (result.payload) {
        setUserData(result.payload);
        setLoading(false);
      }
    });
  }, []);

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  const getDefaultStatus = () => {
    const defaultStatus = options.find((option) => {
      return (
        option.ui_name === "Drawing" &&
        option.field_name === "Status" &&
        option.is_default === true
      );
    });
    return defaultStatus ? defaultStatus.id : "";
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    values.opportunity_id = opportunityData.id;
    values.opportunity_uniqueId = opportunityData.uniqueId;
    values.createdBy = JSON.parse(localStorage.getItem("userSession")).id;

    const formData = new FormData();
    Object.keys(values).forEach((fieldName) => {
      formData.append(fieldName, values[fieldName]);
    });

    await dispatch(addDrawing(formData)).then((result) => {
      if (result.payload) {
        resetForm();
        navigate(`/opportunity/${id}/drawing/${result.payload.result.id}`);
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
  };
  return (
    <>
      <div className="block md:grid grid-cols-3 items-center mb-[8px]">
        <div className="flex gap-[16px] items-center mb-[30px] mb-[8px] md:mb-0">
          <Link to={`/opportunity/${id}`}>
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Drawing / Request
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Request Drawing
            </span>
          </div>
        </div>
        <div className="font-Inter font-[500] leading-[30px] text-[18px] text-center ">
          {opportunityData.id}-{opportunityData.opportunity_name}
        </div>
      </div>
      {loading ? (
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2xl"
            style={{ color: "#55A14A" }}
            spin
          />
        </div>
      ) : (
        <Formik
          initialValues={{
            requested_by: JSON.parse(localStorage.getItem("userSession")).id,
            drawing_revision: "",
            date_required: "",
            status: getDefaultStatus(),
            notes: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            isSubmitting,
            values,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
          }) => (
            <Form>
            <div className="bg-[#F8F8F8] rounded-[12px] p-6 flex flex-col gap-6">
              
              {/* ROW 1: First 3 input fields in one line, 1/3 width each */}
              <div className="flex flex-wrap md:flex-nowrap gap-4">
                {/* Requested By */}
                <div className="flex flex-col w-full md:w-1/3">
                  <label className="font-Inter font-[400] text-[15px] text-[#9E9E9E] mb-1" htmlFor="requested_by">
                    Requested By
                  </label>
                  <div className="relative">
                    <Field
                      as="select"
                      className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:border-gray-500"
                      id="requested_by"
                      name="requested_by"
                      disabled
                    >
                      <option value={""}>Select User</option>
                      {userData.map((user) => (
                        <option value={user.id} key={user.id}>
                          {user.firstname} {user.lastname}
                        </option>
                      ))}
                    </Field>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  {errors.requested_by && touched.requested_by && <div className="text-red-700 text-sm">{errors.requested_by}</div>}
                </div>

                {/* Date Required */}
                <div className="flex flex-col w-full md:w-1/3">
                  <label className="font-Inter font-[400] text-[15px] text-[#9E9E9E] mb-1" htmlFor="date_required">
                    Date Required
                  </label>
                  <Field
                    className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:border-gray-500"
                    type="date"
                    id="date_required"
                    name="date_required"
                  />
                  {errors.date_required && touched.date_required && <div className="text-red-700 text-sm">{errors.date_required}</div>}
                </div>

                {/* Status */}
                <div className="flex flex-col w-full md:w-1/3">
                  <label className="font-Inter font-[400] text-[15px] text-[#9E9E9E] mb-1" htmlFor="status">
                    Status
                  </label>
                  <div className="relative">
                    <Field
                      as="select"
                      className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:border-gray-500"
                      id="status"
                      name="status"
                    >
                      <option value={""}>Select Status</option>
                      {options
                        .filter((option) => option.ui_name === "Drawing" && option.field_name === "Status")
                        .sort((a, b) => a.sort_field - b.sort_field)
                        .map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.field_code}
                          </option>
                        ))}
                    </Field>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  {errors.status && touched.status && <div className="text-red-700 text-sm">{errors.status}</div>}
                </div>
              </div>

              {/* ROW 2: Notes field with 1/4 width */}
              <div className="flex flex-col w-full md:w-1/4">
                <label className="font-Inter text-[15px] text-[#9E9E9E] mb-1">Notes</label>
                <Field
                  as="textarea"
                  name="notes"
                  className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px] h-[52px]"
                />
              </div>

              {/* ROW 3: Save Button (Natural width or full width, placed below) */}
              <div className="w-full md:w-[150px]">
                <button
                  type="submit"
                  className={`py-[12px] px-[32px] text-center text-white w-full rounded-[12px] text-[18px] transition-all ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20] hover:bg-black"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
                </button>
              </div>

            </div>
          </Form>
          )}
        </Formik>
      )}
    </>
  );
}

export default AddDrawing;
